import { useEffect, useMemo, useState } from 'react';
import { useProxyData } from '../contexts/ProxyDataContext';
import { useEmailInbox } from '../contexts/EmailInboxContext';
import { useTeamMessages } from '../contexts/TeamMessagesContext';
import { getMaterialReceipts } from '../services/materialReceiptService';
import type { MaterialReceiptRow } from '../types/api';
import type { ProxyWorkflowContext } from '../data/proxyWorkflowRegistry';

// Assembles the ProxyWorkflowContext every registry workflow's buildTasks
// reads — jobs + emails are already local/synchronous; receipts is a live
// getMaterialReceipts() fetch (needed by cross-section workflows like
// SHORTAGE_FOLLOWUP), polled the same 15s cadence as Sidebar's own Material
// Intake badge count. Degrades gracefully: a failed/slow fetch just leaves
// receipts null, which every receipt-reading workflow already treats as
// "no cross-section match yet," not an error.
export function useProxyWorkflowContext(): ProxyWorkflowContext {
  const { jobs } = useProxyData();
  const { proxyEmails } = useEmailInbox();
  const { messages: teamMessages } = useTeamMessages();
  const [receipts, setReceipts] = useState<MaterialReceiptRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => getMaterialReceipts()
      .then(rows => { if (alive) setReceipts(rows); })
      .catch(() => { /* backend unavailable — cross-section tasks just stay empty */ });
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  return useMemo(() => ({ jobs, emails: proxyEmails, receipts, teamMessages }), [jobs, proxyEmails, receipts, teamMessages]);
}
